/**
 * Detect "is this paste actually code?" from a `ClipboardEvent`.
 *
 * Lead signal: `text/html` clipboard. When you copy from VS Code / a
 * browser code-viewer / a GitHub gist / Linear / Notion / etc., the
 * clipboard carries HTML alongside plain text. The HTML almost always
 * wraps the snippet in `<pre>` (or `<pre><code>`) and frequently
 * carries a `class="language-XX"` indicator that tells us the source
 * language outright.
 *
 * Returning `null` from `detectCodeFromClipboard` means "treat as a
 * normal paste"; the caller falls back to default Tiptap behavior.
 *
 * Why HTML and not text-content heuristics: aggressively wrapping
 * plain-text pastes that "look code-shaped" produces false positives
 * (paragraphs of prose with parens / brackets / semicolons in them
 * get misclassified). The `<pre>` wrapper is a strong, intentional
 * signal — when an IDE / docs site / source-viewer puts something on
 * the clipboard as `<pre>`, the user's intent is unambiguous.
 */

export interface DetectedCode {
  /** Plain-text payload (what we INSERT into the codeBlock node). */
  text: string;
  /** Inferred language tag (e.g. "typescript"); null if unknown. */
  language: string | null;
}

const LANGUAGE_CLASS_RE = /language-(\S+)/;
const HLJS_CLASS_RE = /\bhljs language-(\S+)|hljs-(\S+)/;
const PRISM_CLASS_RE = /\bprism\s+language-(\S+)/;

export function detectCodeFromClipboard(
  event: ClipboardEvent,
): DetectedCode | null {
  const html = event.clipboardData?.getData("text/html");
  if (!html) return null;

  // Parse the clipboard HTML in a sandboxed document so the page's
  // running scripts can't be triggered by a malicious clipboard.
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return null;
  }

  // Strategy 1: an entire-clipboard <pre> → almost certainly code.
  // VS Code's web copy, GitHub raw view, Stackoverflow, MDN, Linear,
  // etc. all produce this shape. Walk the body's direct children and
  // look for the first <pre>; if every other top-level node is empty
  // text, treat the whole paste as code.
  const body = doc.body;
  if (!body) return null;

  const directPre = findDominantPre(body);
  if (directPre) {
    const text = preserveCodeText(directPre);
    if (!text.trim()) return null;
    return { text, language: extractLanguage(directPre) };
  }

  return null;
}

/**
 * Return the `<pre>` element if it dominates the clipboard's body
 * (i.e. the body contains only that pre + whitespace text). Returns
 * null if the body is mixed prose-and-code (we don't want to swallow
 * an entire paragraph of prose just because there's a `<pre>` in it).
 */
function findDominantPre(root: Element): HTMLPreElement | null {
  const pres = root.querySelectorAll("pre");
  if (pres.length === 0) return null;

  // Single <pre> with no significant non-pre siblings → dominant.
  if (pres.length === 1) {
    const pre = pres[0] as HTMLPreElement;
    const others = Array.from(root.querySelectorAll("body > *")).filter(
      (el) => el !== pre && el.textContent && el.textContent.trim().length > 0,
    );
    if (others.length === 0) return pre;
  }

  return null;
}

function extractLanguage(pre: HTMLPreElement): string | null {
  // Class on the <pre> itself.
  const direct = matchLanguageClass(pre.className);
  if (direct) return direct;
  // Class on a wrapping or inner <code>.
  const inner = pre.querySelector("code");
  if (inner) {
    const fromInner = matchLanguageClass(inner.className);
    if (fromInner) return fromInner;
  }
  // Some clipboard sources put the language on a wrapping div / parent.
  let parent: HTMLElement | null = pre.parentElement;
  for (let depth = 0; parent && depth < 3; depth++) {
    const fromParent = matchLanguageClass(parent.className);
    if (fromParent) return fromParent;
    parent = parent.parentElement;
  }
  return null;
}

function matchLanguageClass(className: string | undefined): string | null {
  if (!className) return null;
  const m =
    className.match(LANGUAGE_CLASS_RE) ??
    className.match(HLJS_CLASS_RE) ??
    className.match(PRISM_CLASS_RE);
  if (!m) return null;
  return m[1] ?? m[2] ?? null;
}

/**
 * Pull plain-text out of the <pre>, preserving whitespace exactly.
 * `textContent` is correct here (not `innerText`) — innerText collapses
 * whitespace per CSS rules, which would mangle the indentation.
 */
function preserveCodeText(pre: HTMLPreElement): string {
  return pre.textContent ?? "";
}
